import { useQuery } from '@tanstack/react-query';
import { URLS } from '../constants';
import { API } from '../utils/API';
import { getMenuId, getUserData } from '../utils/helpers';
import store from 'store';

/**
 * Hook to fetch languages from the API
 */
export const useLanguages = (authToken) => {
  return useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      console.log('Fetching languages from:', URLS.languages);
      try {
        const response = await API.get(URLS.languages);
        console.log('Languages response:', response);
        console.log('Languages data:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching languages:', error);
        throw error;
      }
    },
    staleTime: 60 * 60 * 1000, // 1 hour - languages don't change often
    enabled: !!authToken, // Only fetch when user is authenticated
    retry: 3, // Retry failed requests
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus for languages
  });
};

/**
 * Hook to fetch banners from the API
 */
export const useBanners = () => {
  return useQuery({
    queryKey: ['banners'],
    queryFn: async () => {
      const response = await API.get(URLS.banners);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - banners may change more frequently
  });
};

/**
 * Hook to get menu data from stored user information
 * Uses real user data from JWT and fetched languages
 */
export const useMenuData = () => {
  const authToken = store.get('authenticationToken');
  const { data: languages } = useLanguages(authToken);

  return useQuery({
    queryKey: ['menu', languages],
    queryFn: async () => {
      const userData = getUserData();
      const menuId = getMenuId();

      if (!userData || !menuId) {
        // Fallback for development/testing when no user is logged in
        return {
          id: null,
          languages: languages || [
            { id: 1, name: 'العربية' }, // Arabic
            { id: 2, name: 'English' },  // English
            { id: 3, name: 'کوردی' }     // Kurdish
          ]
        };
      }

      // Return real user menu data with fetched languages
      return {
        id: menuId,
        languages: languages || [
          { id: 1, name: 'العربية' }, // Arabic
          { id: 2, name: 'English' },  // English
          { id: 3, name: 'کوردی' }     // Kurdish
        ],
        user: userData
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!languages || true, // Allow fallback even if languages fail
  });
};

/**
 * Hook to fetch paginated categories
 */
export const useCategories = (menuId, page = 1, perPage = 20) => {
  return useQuery({
    queryKey: ['categories', menuId, page, perPage],
    queryFn: async () => {
      const response = await API.get(`${URLS.getAllCategories(menuId)}?page=${page}&per_page=${perPage}`);
      return response.data;
    },
    enabled: !!menuId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch paginated products
 */
export const useProducts = (menuId, page = 1, perPage = 50) => {
  return useQuery({
    queryKey: ['products', menuId, page, perPage],
    queryFn: async () => {
      const response = await API.get(`${URLS.getAllProducts(menuId)}?page=${page}&per_page=${perPage}`);
      return response.data;
    },
    enabled: !!menuId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to search products with filters
 */
export const useSearchProducts = (menuId, searchParams) => {
  return useQuery({
    queryKey: ['search-products', menuId, searchParams],
    queryFn: async () => {
      const response = await API.post(URLS.searchProducts(menuId), searchParams);
      return response.data;
    },
    enabled: !!menuId && !!searchParams,
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
  });
};

/**
 * Staff bulk search with boolean query (AND/OR/NOT) and extended filters.
 */
export const useStaffProductSearch = (searchParams) => {
  return useQuery({
    queryKey: ['staff-product-search', searchParams],
    queryFn: async () => {
      const response = await API.post(URLS.staffProductSearch(), searchParams);
      return response.data;
    },
    enabled: !!searchParams,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Hook to fetch individual product details
 */
export const useProductDetails = (productId) => {
  return useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const response = await API.get(URLS.productDetails(productId));
      return response.data;
    },
    enabled: !!productId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch individual category details
 */
export const useCategoryDetails = (categoryId) => {
  return useQuery({
    queryKey: ['category', categoryId],
    queryFn: async () => {
      const response = await API.get(URLS.categoryDetails(categoryId));
      return response.data;
    },
    enabled: !!categoryId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch individual subcategory details
 */
export const useSubcategoryDetails = (subcategoryId) => {
  return useQuery({
    queryKey: ['subcategory', subcategoryId],
    queryFn: async () => {
      const response = await API.get(URLS.subcategoryDetails(subcategoryId));
      return response.data;
    },
    enabled: !!subcategoryId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch paginated subcategories
 */
export const useSubcategories = (menuId, page = 1, perPage = 20) => {
  return useQuery({
    queryKey: ['subcategories', menuId, page, perPage],
    queryFn: async () => {
      const response = await API.get(`${URLS.getAllSubcategories(menuId)}?page=${page}&per_page=${perPage}`);
      return response.data;
    },
    enabled: !!menuId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch paginated brands
 */
export const useBrands = (menuId, page = 1, perPage = 20) => {
  return useQuery({
    queryKey: ['brands', menuId, page, perPage],
    queryFn: async () => {
      const response = await API.get(`${URLS.getAllBrands(menuId)}?page=${page}&per_page=${perPage}`);
      return response.data;
    },
    enabled: !!menuId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch paginated addons for a menu
 */
export const useAddons = (menuId, page = 1, perPage = 50) => {
  return useQuery({
    queryKey: ['addons', menuId, page, perPage],
    queryFn: async () => {
      const response = await API.get(`${URLS.getAllAddons(menuId)}?page=${page}&per_page=${perPage}`);
      return response.data;
    },
    enabled: !!menuId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch pricing tiers for the current menu/user.
 */
export const usePricingTiers = () => {
  return useQuery({
    queryKey: ['pricing-tiers'],
    queryFn: async () => {
      const response = await API.get(URLS.pricingTiers());
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Hook to search products within a selected pricing tier.
 */
export const usePricingTierProducts = (tierId, searchParams) => {
  return useQuery({
    queryKey: ['pricing-tier-products', tierId, searchParams],
    queryFn: async () => {
      const response = await API.post(URLS.searchPricingTierProducts({ tierId }), searchParams);
      return response.data;
    },
    enabled: !!tierId && !!searchParams,
    staleTime: 60 * 1000,
  });
};

/**
 * Hook to extract tags from menu data
 */
export const useTags = () => {
  const { data: menuData } = useMenuData();
  return {
    tags: menuData?.tags || [],
    isLoading: !menuData,
  };
};

/**
 * Hook to extract banner images from menu data
 */
export const useBannerImages = () => {
  const { data: menuData } = useMenuData();
  return {
    bannerImages: menuData?.banner_images || [],
    isLoading: !menuData,
  };
};
